import { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react'
import { ModalContext } from 'components/Modals/ModalContext'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import kinesisLogo from 'media/kinesis_logo.png'
import moment from 'moment'

import KinesisProgramWeekBar from 'components/Athlete/v2/KinesisProgramWeeks/KinesisProgramWeekBar'

const getSegmentDomainForChartWidth = (widthPx) => {
    // console.log('widthPx', widthPx)
    if (widthPx >= 1200) return 33
    if (widthPx >= 800) return 23
    return 15
}

export default function KinesisProgramWeeks({
    displaySize = 'small',
    presentWeekAlignment = 'center',
    disableNaivationWhenModalIsOpen = false,
    trainingChartPresentWeekOfTheYearKey,
    trainingChartSelectedWeekOfTheYearKey,
    trainingChartSegmentDomain,
    trainingChartSegment,
    kinesisEvents,
    NavigateCalendarToPreviousWeek = () => { },
    NavigateCalendarToSpecificWeek = () => { },
    NavigateCalendarToNextWeek = () => { },
    onSegmentDomainFromWidth,
}) {
    const { modalData } = useContext(ModalContext)

    const chartGridRef = useRef(null)
    const onSegmentDomainFromWidthRef = useRef(onSegmentDomainFromWidth)
    onSegmentDomainFromWidthRef.current = onSegmentDomainFromWidth

    const selectedTrainingChartColumn = trainingChartSegment.find(trainingChartData => new WeekOfTheYearKey(trainingChartData.weekOfTheYearKey).equals(new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey)))

    const [nextEventInfo, setNextEventInfo] = useState(null)
    const [daysUntilNextRace, setDaysUntilNextRace] = useState(0)
    const [selectedProgramPhaseKey, setSelectedProgramPhaseKey] = useState('')
    const [selectedProgramWeekKey, setSelectedProgramWeekKey] = useState('')

    const UpdateDaysUntilNextRace = () => {

        var countdownStartingDate = null

        // If the present week is currently being viewed, use the exact present date
        if (new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).equals(new WeekOfTheYearKey(trainingChartPresentWeekOfTheYearKey))) {
            countdownStartingDate = moment.utc().local()
        }
        // Otherwise, use the start of the week of the selected week
        else {
            countdownStartingDate = new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).toDate();
        }

        // Sort events by event data and convert eventDate to moment object if it's a string

        var sortedKinesisEvents = JSON.parse(JSON.stringify(kinesisEvents))

        if (sortedKinesisEvents && Array.isArray(sortedKinesisEvents)) {
            sortedKinesisEvents.forEach(event => {
                if (event.eventDate && typeof event.eventDate === 'string') {
                    event.eventDate = moment(event.eventDate)
                }
            })
        }

        sortedKinesisEvents.sort((a, b) => a.eventDate.diff(b.eventDate))

        // Calculate days until next race

        const nextUpcomingRace = sortedKinesisEvents.find(event => event.eventDate.isAfter(countdownStartingDate));

        const daysUntilNextRace = nextUpcomingRace ? nextUpcomingRace.eventDate.diff(countdownStartingDate, 'days') : 0

        setNextEventInfo(nextUpcomingRace)
        setDaysUntilNextRace(daysUntilNextRace)
    }

    const UpdateCurrentSeasonPhaseName = () => {

        var programPhaseKey = ''
        var programWeekKey = ''

        switch (selectedTrainingChartColumn.trainingChartDataKey) {

            case 'empty':
                programPhaseKey = 'no-training';
                programWeekKey = 'none';
                break;

            case 'past':
                programPhaseKey = selectedTrainingChartColumn.overviewFile?.programPhaseKey;
                programWeekKey = selectedTrainingChartColumn.overviewFile?.programWeekKey;
                break;

            case 'active':
                programPhaseKey = selectedTrainingChartColumn.overviewFile?.programPhaseKey;
                programWeekKey = selectedTrainingChartColumn.overviewFile?.programWeekKey;
                break;

            case 'future':
                programPhaseKey = selectedTrainingChartColumn.programTableColumn?.volumeData?.programPhaseKey;
                programWeekKey = selectedTrainingChartColumn.programTableColumn?.volumeData?.programWeekKey;
                break;

            default:
                programPhaseKey = 'no-training';
                programWeekKey = 'none';
                break;
        }

        // if (!seasonPhaseName) seasonPhaseName = 'No Training';

        // Capitalize first letter
        // seasonPhaseName = seasonPhaseName.charAt(0).toUpperCase() + seasonPhaseName.slice(1)

        setSelectedProgramPhaseKey(programPhaseKey)
        setSelectedProgramWeekKey(programWeekKey)
    }

    const GetHeightForTaperLevel = (taperLevel) => {

        if (displaySize === 'large') {
            switch (taperLevel) {
                case 'taper-none': return 'h-52'
                case 'taper-one': return 'h-40'
                case 'taper-two': return 'h-32'
                case 'taper-three': return 'h-20'
                default: return 'h-8'
            }
        }

        switch (taperLevel) {
            case 'taper-none': return 'h-20'
            case 'taper-one': return 'h-16'
            case 'taper-two': return 'h-12'
            case 'taper-three': return 'h-8'
            default: return 'h-2'
        }
    }

    const GetProgramWeekBarStyle = (trainingChartWeekData) => {

        if (trainingChartWeekData.trainingChartDataKey === 'empty') {
            return 'w-full rounded-md bg-gray-300 ' + GetHeightForTaperLevel('default')
        }

        var taperLevel = 'default'

        if (
            trainingChartWeekData.trainingChartDataKey === 'past' ||
            trainingChartWeekData.trainingChartDataKey === 'active'
        ) {
            taperLevel = trainingChartWeekData.overviewFile.taperData.taperLevel
        }

        if (trainingChartWeekData.trainingChartDataKey === 'future') {
            taperLevel = trainingChartWeekData.programTableColumn.volumeData.taperData.taperLevel
        }

        var rounding = 'rounded-md'
        if (displaySize === 'large') rounding = 'rounded-xl'

        return 'w-full relative bg-orange-300 ' + rounding + ' ' + GetHeightForTaperLevel(taperLevel)
    }

    const GetEventsInWeek = (weekOfTheYearKey) => {

        const eventsInWeek = []

        const weekDate = new WeekOfTheYearKey(weekOfTheYearKey).toDate()
        const weekStart = moment(weekDate).startOf('isoWeek')
        const weekEnd = moment(weekDate).endOf('isoWeek')

        kinesisEvents.forEach(event => {
            const eventDate = moment(event.eventDate)

            if (eventDate.isBetween(weekStart, weekEnd, 'day', '[]')) {
                eventsInWeek.push(event)
            }
        })

        return eventsInWeek
    }

    const GetProgramWeekKey = (trainingChartWeekData) => {

        var programWeekKey = ''

        switch (trainingChartWeekData.trainingChartDataKey) {

            case 'empty':
                // programPhaseKey = 'no-training';
                programWeekKey = 'none';
                break;

            case 'past':
                // programPhaseKey = trainingChartWeekData.overviewFile?.programPhaseKey; 
                programWeekKey = trainingChartWeekData.overviewFile?.programWeekKey;
                break;

            case 'active':
                // programPhaseKey = trainingChartWeekData.overviewFile?.programPhaseKey;
                programWeekKey = trainingChartWeekData.overviewFile?.programWeekKey;
                break;

            case 'future':
                // programPhaseKey = trainingChartWeekData.programTableColumn?.volumeData?.programPhaseKey;
                programWeekKey = trainingChartWeekData.programTableColumn?.volumeData?.programWeekKey;
                break;

            default:
                // programPhaseKey = 'no-training';
                programWeekKey = 'none';
                break;
        }

        return programWeekKey
    }

    const GetWeekCompletionStatusIconKey = (trainingChartWeekData) => {
        if (trainingChartWeekData.trainingChartDataKey === 'past') {
            return trainingChartWeekData.trainingChartDataKey + '-' + trainingChartWeekData.overviewFile.weekCompletionStatusKey
        }

        if (
            trainingChartWeekData.trainingChartDataKey === 'active'
            && trainingChartWeekData.overviewFile.weekCompletionStatusKey === 'skipped'
        ) {
            return trainingChartWeekData.trainingChartDataKey + '-' + trainingChartWeekData.overviewFile.weekCompletionStatusKey
        }

        return trainingChartWeekData.trainingChartDataKey
    }

    const OnSetWeekButton = (trainingChartWeekIndex) => {
        if (presentWeekAlignment === 'center') {
            const offset = trainingChartWeekIndex - Math.floor(trainingChartSegmentDomain / 2)
            const newWeekOfTheYearKey = new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).add(offset)
            NavigateCalendarToSpecificWeek(newWeekOfTheYearKey)
        }

        if (presentWeekAlignment === 'left') {
            const newWeekOfTheYearKey = new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).add(trainingChartWeekIndex)
            NavigateCalendarToSpecificWeek(newWeekOfTheYearKey)
        }
    }

    useEffect(() => {

        const handleKeyDown = (e) => {

            if (disableNaivationWhenModalIsOpen && modalData !== null) return

            if (e.key === 'ArrowLeft') NavigateCalendarToPreviousWeek()
            if (e.key === 'ArrowRight') NavigateCalendarToNextWeek()
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)

    }, [trainingChartSelectedWeekOfTheYearKey, disableNaivationWhenModalIsOpen, modalData, NavigateCalendarToPreviousWeek, NavigateCalendarToNextWeek])

    useEffect(() => {
        if (kinesisEvents !== null && selectedTrainingChartColumn) {
            UpdateDaysUntilNextRace()
            UpdateCurrentSeasonPhaseName()
        }
    }, [kinesisEvents, selectedTrainingChartColumn])

    const syncSegmentDomainToWidth = Boolean(onSegmentDomainFromWidth)

    useLayoutEffect(() => {
        if (!syncSegmentDomainToWidth) return undefined

        const node = chartGridRef.current
        if (!node) return undefined

        let lastDomain = null

        const syncFromWidth = (widthPx) => {
            if (widthPx < 48) return
            const next = getSegmentDomainForChartWidth(widthPx)
            if (next !== lastDomain) {
                lastDomain = next
                onSegmentDomainFromWidthRef.current?.(next)
            }
        }

        const ro = new ResizeObserver((entries) => {
            const entry = entries[0]
            if (!entry) return
            syncFromWidth(entry.contentRect.width)
        })

        ro.observe(node)
        syncFromWidth(node.getBoundingClientRect().width)

        return () => ro.disconnect()
    }, [syncSegmentDomainToWidth])

    return (
        <>
            <div
                className={`
                    w-full flex items-start justify-center select-none
                    ${displaySize === 'small' ? 'h-[110px]' : ''}
                `}
            >
                <div className="w-full sm:w-1/3 flex flex-col items-center text-center pt-1 sm:pt-2">

                    {displaySize === 'large' &&
                        <img src={kinesisLogo} alt="Kinesis Logo" className="h-32 w-32 hidden sm:block" />
                    }

                    <div
                        className={`
                            font-semibold
                            ${displaySize === 'small' ? 'text-xs sm:text-sm 2xl:text-base' : ''}
                            ${displaySize === 'large' ? 'text-xl 2xl:text-2xl' : ''}
                            mb-0.5 sm:mb-1
                            px-1
                        `}
                    >
                        {new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).toDateRangeString('MMMM D, YYYY')}
                    </div>

                    <div className='flex items-center justify-center gap-1 sm:gap-2 mb-1 sm:mb-2 text-xxs sm:text-xs'>
                        {nextEventInfo && (
                            <>
                                <div className='h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-orange-400 animate-pulse' />
                                <span
                                    className={`
                                        ${displaySize === 'small' ? 'text-xxs sm:text-xs' : ''}
                                        ${displaySize === 'large' ? 'text-base' : ''}
                                    `}
                                >
                                    {daysUntilNextRace} days to {nextEventInfo.name}
                                </span>
                            </>
                        )}

                        {!nextEventInfo &&
                            <span
                                className={`
                                    ${displaySize === 'small' ? 'text-xxs sm:text-xs' : ''}
                                    ${displaySize === 'large' ? 'text-base' : ''}
                                `}
                            >
                                No Events
                            </span>
                        }
                    </div>

                    <div 
                        className={`
                            px-2 sm:px-4 bg-orange-100 border border-orange-400 rounded-full
                            ${displaySize === 'small' ? 'text-xxs sm:text-xs py-0.5' : ''}
                            ${displaySize === 'large' ? 'text-sm 2xl:text-base py-1 mb-4' : ''}
                        `}
                    >
                        {selectedProgramPhaseKey
                            .split('-')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                        }
                    </div>
                </div>
            </div>

            <div
                className={`
                    w-full flex min-h-0 overflow-y-hidden pb-1 sm:pb-2
                    ${displaySize === 'small' ? 'h-[100px] sm:h-[185px]' : ''}
                    ${displaySize === 'large' ? 'h-[350px]' : ''}
                `}
            >
                {/* Past Week Navigation */}
                <div
                    className="h-full flex-grow flex items-center justify-start cursor-pointer select-none"
                    onClick={() => NavigateCalendarToPreviousWeek()}
                >
                    <ChevronLeftIcon 
                        className={`
                            pl-1 sm:pl-2 text-gray-500 pointer-events-none
                            ${displaySize === 'small' ? 'h-6 sm:h-8' : ''}
                            ${displaySize === 'large' ? 'h-12' : ''}
                        `} 
                    />
                </div>

                {/* Program Week Bar Visualization */}
                <div
                    ref={chartGridRef}
                    className="grid h-full min-h-0 min-w-0 w-full overflow-x-auto overflow-y-hidden px-1 sm:px-2"
                    style={{ gridTemplateColumns: `repeat(${trainingChartSegmentDomain}, minmax(0, 1fr))` }}
                >
                    {trainingChartSegment.map((trainingChartWeekData, trainingChartWeekIndex) => (
                        <KinesisProgramWeekBar
                            key={trainingChartWeekIndex}
                            displaySize={displaySize}
                            isCurrentlySelectedWeek={new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).equals(new WeekOfTheYearKey(trainingChartWeekData.weekOfTheYearKey))}
                            isPresentWeek={new WeekOfTheYearKey(trainingChartPresentWeekOfTheYearKey).equals(new WeekOfTheYearKey(trainingChartWeekData.weekOfTheYearKey))}
                            trainingChartWeekIndex={trainingChartWeekIndex}
                            trainingChartWeekData={trainingChartWeekData}
                            programWeekBarStyle={GetProgramWeekBarStyle(trainingChartWeekData)}
                            eventsInWeek={GetEventsInWeek(trainingChartWeekData.weekOfTheYearKey)}
                            programWeekKey={GetProgramWeekKey(trainingChartWeekData)}
                            weekCompletionStatusIconKey={GetWeekCompletionStatusIconKey(trainingChartWeekData)}
                            OnSetWeekButton={OnSetWeekButton}
                        />
                    ))}
                </div>

                {/* Future Week Navigation */}
                <div
                    className="h-full flex-grow flex items-center justify-end cursor-pointer select-none"
                    onClick={() => NavigateCalendarToNextWeek()}
                >
                    <ChevronRightIcon 
                        className={`
                            pr-1 sm:pr-2 text-gray-500 pointer-events-none
                            ${displaySize === 'small' ? 'h-6 sm:h-8' : ''}
                            ${displaySize === 'large' ? 'h-12' : ''}
                        `}
                    />
                </div>

            </div>
        </>
    )
}
